import {
  Component,
  ContentChild,
  Directive,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IProjectGroup } from '@core/state/entities/project-group/project-group.entity';
import _ from 'lodash-es';

import { FullName } from '@core/services/api-client.generated';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { FullNamePipe } from '@shared/pipes/full-name/full-name.pipe';
import { AvatarIconComponent } from '../../../../shared/avatar-icon/avatar-icon.component';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { NgTemplateOutlet, NgClass, TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { DebuggerComponent } from '../../../../shared/debugger/debugger.component';
import { DebuggerGroupComponent } from '../../../../shared/debugger-group/debugger-group.component';

class ItemActionContext {
  $implicit: IMemberTableItem;
}

@Directive({
  selector: '[appItemAction]',
  standalone: true,
})
export class ItemActionDirective {}

class TableSubtextContext {
  $implicit: number;
  collectiveName: string;
}

@Directive({
  selector: '[appTableSubtext]',
  standalone: true,
})
export class TableSubtextDirective {}

export interface IMemberTableItem {
  tableId: string;
  id: string;
  investigatorName?: FullName | null;
  email?: string;
  groups?: IProjectGroup[];
  pictureUrl?: string | null;
  dateTimeCreated: string;
  isOwner?: boolean;
  isCurrent?: boolean;
}

class SelectedItemsActionsContext {
  $implicit: string[];
}

@Directive({
  selector: '[appSelectedItemsActions]',
  standalone: true,
})
export class SelectedItemsActionsDirective {}

@Component({
  selector: 'app-membership-table',
  templateUrl: './membership-table.component.html',
  styleUrls: ['./membership-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DebuggerGroupComponent,
    DebuggerComponent,
    MatButtonModule,
    MatMenuModule,
    FlexLayoutModule,
    MatIconModule,
    NgTemplateOutlet,
    MatTableModule,
    MatCheckboxModule,
    FormsModule,
    NgClass,
    AvatarIconComponent,
    TitleCasePipe,
    FullNamePipe,
  ],
})
export class MembershipTableComponent implements OnChanges {
  @Input() showEmail = false;
  @Input() title: string;
  @Input() singularItemName: any;
  @Input() pluralItemName: any;
  @Input() members: IMemberTableItem[] = [];
  @Input() showAvatar = false;
  @Input() showGroup = true;
  @Input() showName = true;
  @ContentChild(ItemActionDirective, { read: TemplateRef<ItemActionContext> })
  itemActionTemplate: TemplateRef<ItemActionContext>;
  @ContentChild(TableSubtextDirective, {
    read: TemplateRef<TableSubtextContext>,
  })
  subtextTemplate: TemplateRef<TableSubtextContext>;
  @ContentChild(SelectedItemsActionsDirective, {
    read: TemplateRef<SelectedItemsActionsContext>,
  })
  selectedItemsActionsTemplate: TemplateRef<SelectedItemsActionsContext>;
  selectedMembers: string[] = [];
  get displayedColumns(): string[] {
    return [
      'selection',
      ...(this.showName ? ['name'] : []),
      ...(this.showEmail ? ['email'] : []),
      ...(this.showGroup ? ['groups'] : []),
      'actions',
    ];
  }
  get memberCount() {
    return this.members?.length;
  }

  get selectedCount() {
    return this.selectedMembers.length;
  }

  get allSelected() {
    return (
      this.memberCount > 0 &&
      this.selectedMembers.length === this.members.length
    );
  }

  get someSelected() {
    return !this.allSelected && this.selectedCount > 0;
  }

  get anySelected() {
    return this.selectedCount > 0;
  }

  get collectiveName() {
    return this.memberCount === 1 ? this.singularItemName : this.pluralItemName;
  }

  toggleSelectAll() {
    this.selectedMembers = this.allSelected
      ? []
      : this.members.map((i) => i.id);
  }

  isAdmin(member: IMemberTableItem) {
    return member.groups?.some((g) => g.name === 'Administrator') ?? false;
  }

  selectItem(changeEvent: MatCheckboxChange, itemId: string) {
    this.selectedMembers = changeEvent.checked
      ? _.union(this.selectedMembers, [itemId])
      : _.difference(this.selectedMembers, [itemId]);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.members &&
      !changes.members.firstChange &&
      JSON.stringify(
        (changes.members.currentValue as IMemberTableItem[])?.map((m) => m.id)
      ) !==
        JSON.stringify(
          (changes.members.previousValue as IMemberTableItem[])?.map(
            (m) => m.id
          )
        )
    ) {
      this.selectedMembers = [];
    }
  }
}
